import { BOT_TIMEZONES } from '@/utils/bot.constants'
import botModule from '@/store/modules/bot.module'
import marketModule from '@/store/modules/market.module'
import toaster from '@/utils/toaster'
import { ensureBotAndMarketLists } from '@/utils/bot-market.utils'

/**
 * Mixin for integration form components (Create and Editor)
 * Provides shared data, computed properties, and methods for handling
 * integration forms with bots, timezones, and error handling
 */
export default {
  data() {
    return {
      bots: [],
      fieldErrors: {},
      loaders: {
        bots: false
      }
    }
  },

  computed: {
    /**
     * Format timezone options for queryable select
     * @returns {Array} Array of {text, value} objects
     */
    timezoneOptions() {
      return BOT_TIMEZONES.map((timezone) => ({
        text: timezone,
        value: timezone
      }))
    },

    /**
     * Format bot options for queryable select as "botName: marketName", or "botName" when the bot has no market.
     * Variant bots use the primary bot's name. The bot id is not shown. Options are sorted by that label.
     * @returns {Array} Array of {text, value} objects
     */
    botOptions() {
      if (!this.bots || this.bots.length === 0) return []
      return this.bots.map(bot => {
        const primaryBot = this.bots.find(item => item.id === bot.primary_bot_id) || bot
        const market = marketModule.getMarketList().find(item => item.id === bot.market_id)
        const botName = primaryBot.name || 'Unnamed'

        return {
          text: market?.name ? `${botName}: ${market.name}` : botName,
          value: String(bot.id)
        }
      }).sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }))
    }
  },

  methods: {
    /**
     * Handle bot selection and clear related errors
     * @param {string} botId - Selected bot ID
     */
    handleBotChange(botId) {
      this.selectedBotId = botId
      // Clear bot error when user selects a bot
      if (botId && this.fieldErrors.bots) {
        // eslint-disable-next-line no-unused-vars
        const { bots, ...rest } = this.fieldErrors
        this.fieldErrors = rest
      }
    },

    /**
     * Handle API errors and parse field-specific validation errors
     * @param {Error} err - API error object
     */
    handleApiError(err) {
      const errors = err.response?.data?.errors || err.response?.data?.details?.errors || []
      
      if (Array.isArray(errors) && errors.length > 0) {
        // Parse field-specific errors
        errors.forEach(error => {
          let field = error.field || error.path || error.property
          const message = error.message
          
          if (field && message) {
            // Map nested field paths to form fields
            // e.g., "channels.[0].id" -> "channels"
            field = this.mapApiFieldToFormField(field)
            this.fieldErrors = { ...this.fieldErrors, [field]: message }
          }
        })
        
        // If we have field errors, don't show toaster
        if (Object.keys(this.fieldErrors).length > 0) {
          return
        }
      }
      
      // Check if error message contains field name (e.g., "Property 'email' cannot be empty")
      const errorMessage = err.response?.data?.message || err.message || 'Failed to process integration'
      const fieldMatch = errorMessage.match(/Property '(\w+)' cannot be empty/i)
      
      if (fieldMatch && fieldMatch[1]) {
        const field = fieldMatch[1]
        this.fieldErrors = { ...this.fieldErrors, [field]: errorMessage }
      } else {
        // Generic error
        this.error = errorMessage
        toaster.error(errorMessage)
      }
    },

    /**
     * Map API field names to form field names
     * @param {string} apiField - Field name from API error
     * @returns {string} Mapped form field name
     */
    mapApiFieldToFormField(apiField) {
      // Ensure apiField is a string
      const fieldStr = String(apiField)
      
      // Handle nested paths like "bot_id" -> "bots"
      if (fieldStr === 'bot_id' || fieldStr.startsWith('bot')) {
        return 'bots'
      }
      
      // Add more mappings as needed
      return fieldStr
    },

    /**
     * Load bots for the selector from the shared store list, and markets for labels.
     * A failed market fetch leaves same-named bots indistinguishable, so report it here.
     * @returns {Promise<void>}
     */
    async loadBotOptions() {
      this.loaders.bots = true
      try {
        await ensureBotAndMarketLists(false)
      } catch (err) {
        const failedLists = err.failedLists || []
        if (failedLists.includes('bots')) toaster.error('Failed to load bots')
        if (failedLists.includes('markets')) toaster.error('Failed to load markets')
      } finally {
        this.bots = botModule.getBotList()
        this.loaders.bots = false
      }
    }
  }
}

